import Highcharts, { Options } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsBoost from "highcharts/modules/boost";
import Exporting from "highcharts/modules/exporting";
import HighchartsExporting from "highcharts/modules/exporting";
import React, { FC, useEffect, useRef, useState } from "react";
import OfflineExporting from "highcharts/modules/offline-exporting";

HighchartsBoost(Highcharts);
HighchartsExporting(Highcharts);
Exporting(Highcharts);
OfflineExporting(Highcharts);

interface CustomHighChartComponentProps {
  chartTitle?: string;
  titleText?: string;
  xAxisCategories: any;
  SeriesType: any;
  seriesData?: any;
  seriesShowInLegend: boolean;
  seriesName?: string;
  xAxisTitleText?: string;
  yAxisTitleText?: string;
  colors?: string[];
  containerProps?: any;
  baseLine?: { base: any; ranges: any };
  onClickColumn?: (event: any) => void;
  extraData?: any;
  redirectRoute?: string;
  multipleSeriesData?: any;
  xAxisMin?: any;
  xAxisMax?: any;
  yAxisMin?: any;
  yAxisMax?: any;
  xAxisTimeFormatter?: any;
  setSelectedLocation?: any;
  setActiveKey?: any;
  fileName?: string;
  chartName?: string;
  tooltipFormatter?: (point: any, extraData?: any) => string;
  isEnergyTooltip?: boolean;
  isBurnTimeChart?: boolean;
}

(function (H: typeof Highcharts) {
  H.wrap(H.PlotLineOrBand.prototype, "render", function (this: any, proceed) {
    let options = this.options,
      start = options.start,
      end = options.end,
      value = options.value;

    let result = proceed.apply(this, arguments);
    if (start && end) {
      if (this.axis.coll === "yAxis") {
        result.svgElem.attr({
          d:
            "M " +
            this.axis.chart.xAxis[0].toPixels(start) +
            " " +
            this.axis.chart.yAxis[0].toPixels(value) +
            " L " +
            this.axis.chart.xAxis[0].toPixels(end) +
            " " +
            this.axis.chart.yAxis[0].toPixels(value),
        });
      } else {
        result.svgElem.attr({
          d:
            "M " +
            this.axis.chart.xAxis[0].toPixels(value) +
            " " +
            this.axis.chart.yAxis[0].toPixels(start) +
            " L" +
            this.axis.chart.xAxis[0].toPixels(value) +
            " " +
            this.axis.chart.yAxis[0].toPixels(end),
        });
      }
    }
    return result;
  });
})(Highcharts);

function makeXAxisLabelFormatter(xAxisTimeFormatter: boolean) {
  return function (this: { value: string | number }): string {
    const value = this.value;
    if (xAxisTimeFormatter) {
      const numericValue =
        typeof this.value === "string" ? Number(this.value) : this.value;
      const hours = Math.floor(numericValue);
      return hours.toString().padStart(2, "0");
    }
    return `${value}`;
  };
}

interface TooltipFormatterConfig {
  tooltipFormatter?: (point: any, extraData?: any) => string;
  extraData?: any;
  xAxisTimeFormatter?: boolean;
  isEnergyTooltip?: boolean;
  isBurnTimeChart?: boolean;
}

function makeTooltipFormatter(config: TooltipFormatterConfig) {
  const {
    tooltipFormatter,
    extraData,
    xAxisTimeFormatter,
    isEnergyTooltip,
    isBurnTimeChart,
  } = config;

  return function (this: Highcharts.TooltipFormatterContextObject): string | undefined {
    // Use custom tooltipFormatter if provided
    if (tooltipFormatter) {
      return tooltipFormatter(this.point, extraData);
    }

    // Fallback to default tooltip logic
    const { x, y } = this.point;

    if (xAxisTimeFormatter) {
      const hours = Math.floor(x as number);
      const minutes = Math.round(((x as number) - hours) * 60);
      const time =
        hours.toString().padStart(2, "0") +
        ":" +
        minutes.toString().padStart(2, "0");
      return `<b>Time:</b> ${time}`;
    }

    if (isEnergyTooltip) {
      const item = extraData?.find(
        (data: any) =>
          data?.displayName == this.point.category &&
          data?.energyConsuption == y
      );

      return `<b>Sensor:</b> ${this.point.category
        }<br/><b>Energy Consumed:</b> ${y} Wh<br/><b>Energy Saved:</b> ${item?.energySaved
        }%<br/><b>Level:</b> ${item?.level ? item?.level : "Not Assigned"
        }`;
    }

    if (isBurnTimeChart) {
      const level = extraData?.find(
        (data: any) =>
          data?.displayName == this.point.category &&
          data?.burnTime == y
      )?.level;

      return `<b>Sensor: </b> ${this.point.category
        }<br/><b>Burn Time (hours): </b> ${y}<br/><b>Level: </b> ${level}`;
    }

    return undefined;
  };
}

const CustomHighChartComponent: FC<CustomHighChartComponentProps> = (
  {
    chartTitle,
    titleText,
    xAxisCategories,
    SeriesType,
    seriesData = null,
    seriesShowInLegend,
    seriesName,
    xAxisTitleText,
    yAxisTitleText,
    colors,
    containerProps,
    baseLine,
    onClickColumn,
    extraData,
    redirectRoute,
    multipleSeriesData = null,
    xAxisMin = 0,
    xAxisMax,
    yAxisMin = 0,
    yAxisMax,
    xAxisTimeFormatter = false,
    setActiveKey,
    setSelectedLocation,
    fileName,
    chartName,
    tooltipFormatter,
    isEnergyTooltip = false,
    isBurnTimeChart = false,
  },
  props: HighchartsReact.Props
) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [CustomHighChartOptions, setCustomHighChartOptions] = useState(
    {} as any
  );

  let plotlineObj: any[] = [];

  if (baseLine?.base?.length && baseLine?.ranges?.length) {
    for (let index = 0; index < baseLine?.base?.length; index++) {
      let obj = {
        value: baseLine?.base[index],
        start: baseLine?.ranges[index][0],
        end: baseLine?.ranges[index][1],
      };
      plotlineObj.push(obj);
    }
  } else if (baseLine?.base?.length) {
    for (let index = 0; index < baseLine?.base?.length; index++) {
      let obj = {
        value: baseLine?.base[index],
      };
      plotlineObj.push(obj);
    }
  }

  useEffect(() => {
    if (multipleSeriesData?.length || seriesData?.length) {
      let maximumValue;

      if (seriesData?.length) {
        maximumValue = Math.max(...seriesData);
      } else {
        maximumValue = yAxisMax;
      }

      const options: Options = {
        chart: {
          style: {
            fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
          },
          zooming: {
            type: "x",
          },
          animation: {
            duration: 1500,
          },
        },
        credits: {
          enabled: false,
        },
        boost: {
          useGPUTranslations: true,
          seriesThreshold: 5,
        },
        title: { text: "" },
        xAxis: {
          type: "datetime",
          categories: xAxisCategories?.length ? xAxisCategories : [],
          min: xAxisMin ? xAxisMin : null,
          max: xAxisMax ? xAxisMax : null,
          labels: {
            formatter: makeXAxisLabelFormatter(xAxisTimeFormatter),
          },
          title: {
            text: xAxisTitleText ?? "",
            style: {
              fontSize: "16px",
              fontWeight: "bold",
              textAlign: "right",
            },
          },
        },
        yAxis: {
          min: yAxisMin,
          max: yAxisMax,
          softMax: maximumValue < 1 && maximumValue > 0 ? maximumValue : 5,
          title: {
            text: yAxisTitleText ?? "",
            style: {
              fontSize: "16px",
              fontWeight: "bold",
            },
          },

          plotLines: plotlineObj?.map((baseValue: any) => {
            return baseValue?.start && baseValue?.end
              ? {
                value: baseValue?.value,
                color: "red",
                width: 2,
                label: {
                  text: "",
                  y: 13,
                },
                zIndex: 5,
                start: baseValue?.start,
                end: baseValue?.end,
              }
              : {
                value: baseValue?.value,
                color: "red",
                width: 2,
                label: {
                  text: "",
                  y: 13,
                },
                zIndex: 5,
              };
          }),
        },
        tooltip: {
          formatter: makeTooltipFormatter({
            tooltipFormatter,
            extraData,
            xAxisTimeFormatter,
            isEnergyTooltip,
            isBurnTimeChart,
          }),
        },
        plotOptions: {
          column: {
            pointPadding: 0,
            events: {
              click: function (event: any) {
                if (xAxisTitleText !== "Sensors") {
                  const xLabel = event.point.category;
                  const location = xLabel.split("_")[0];
                  if (setSelectedLocation) setSelectedLocation(location);
                  if (setActiveKey) setActiveKey("Sensor");
                }

                if (onClickColumn) {
                  onClickColumn(event);
                } else if (extraData?.length > 0) {
                  let value;

                  if (isEnergyTooltip) {
                    value = extraData?.find(
                      (data: any) =>
                        data?.displayName == event?.point?.category &&
                        data?.energyConsuption == event?.point?.y
                    );
                  } else {
                    value = extraData?.find(
                      (data: any) => data?.displayName == event?.point?.category
                    );
                  }

                  if (value?.code && redirectRoute) {
                    const route = redirectRoute + `${btoa(value?.code)}`;
                    const newTab = window.open(`${route}`, "_blank");
                    newTab?.focus();
                  }
                } else if (redirectRoute) {
                  const route =
                    redirectRoute + `${btoa(event?.point?.category)}`;
                  const newTab = window.open(`${route}`, "_blank");
                  newTab?.focus();
                }
              },
            },
          },
          line: {
            events: {
              click: function (event: any) {
                if (onClickColumn) {
                  onClickColumn(event);
                } else if (extraData?.length > 0) {
                  const value = extraData?.find(
                    (data: any) => data?.displayName == event?.point?.category
                  );

                  if (value?.code && redirectRoute) {
                    const route = redirectRoute + `${btoa(value?.code)}`;
                    const newTab = window.open(`${route}`, "_blank");
                    newTab?.focus();
                  }
                } else if (redirectRoute) {
                  const route =
                    redirectRoute + `${btoa(event?.point?.category)}`;
                  const newTab = window.open(`${route}`, "_blank");
                  newTab?.focus();
                }
              },
            },
          },
          spline: {
            marker: {
              radius: 4,
              lineColor: "#66b2ff",
              lineWidth: 1,
            },
          },
        },
        series: multipleSeriesData
          ? multipleSeriesData.map((data: any) => {
            return {
              type: SeriesType,
              data: data?.data,
              showInLegend: seriesShowInLegend,
              name: data?.name,
            };
          })
          : [
            {
              type: SeriesType,
              data: seriesData,
              showInLegend: seriesShowInLegend,
              name: seriesName,
              turboThreshold: 0,
              boostThreshold: 0,
            },
          ],
        accessibility: {
          enabled: false,
        },
        colors: colors,

        exporting: {
          enabled: true,
          fallbackToExportServer: false,
          filename: fileName || "chart",
          sourceWidth: 1200, // Increase source dimensions
          sourceHeight: 500,
          scale: 1, // Maintain original scale
          chartOptions: chartName
            ? {
              chart: {
                height: "100%", // Use relative height
                width: "100%", // Use relative width
                spacingTop: 30, // Add more space at the top for the title
                marginTop: 100, // Increase the top margin to push chart content down
              },
              title: {
                text: chartName, // Dynamic title from prop
                align: "center",
                margin: 20,
                style: {
                  fontSize: "24px",
                  fontWeight: "bold",
                },
              },
            }
            : {
              chart: {
                height: "100%", // Use relative height
                width: "100%", // Use relative width
              },
              title: {
                style: {
                  fontSize: "14px",
                },
              },
            },
          buttons: {
            contextButton: {
              menuItems: [
                "viewFullscreen",
                "printChart",
                "separator",
                "downloadPNG",
                "downloadJPEG",
                "downloadPDF",
                "downloadSVG",
              ],
            },
          },
        },
      };

      if (chartComponentRef?.current?.chart) {
        chartComponentRef?.current?.chart?.zoomOut();
      }

      setCustomHighChartOptions(options);
    }
  }, [multipleSeriesData, seriesData, yAxisMax, yAxisMin]);

  return (
    <div>
      {chartTitle ? (
        <h4
          className="h4"
          style={{
            marginLeft: "1rem",
            zIndex: 999,
            position: "relative",
          }}
        >
          {chartTitle}
        </h4>
      ) : null}

      {Object.keys(CustomHighChartOptions).length &&
        CustomHighChartOptions?.series &&
        (multipleSeriesData?.length || seriesData?.length) ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={CustomHighChartOptions}
          ref={chartComponentRef}
          containerProps={containerProps}
          {...props}
        />
      ) : (
        <div
          style={{
            height: "300px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D9D9D9",
            fontSize: "22px",
            fontWeight: "600",
            color: "#6d6d6d",
          }}
        >
          No Data Available
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomHighChartComponent);
